export type StaffCsvRecord = Record<string, string>;

function normalizeCsvHeader(value: string) {
  return value.trim().toLowerCase();
}

function readFirstNonEmptyValue(row: StaffCsvRecord, keys: string[]) {
  for (const key of keys) {
    const value = String(row[key] ?? "").trim();
    if (value) {
      return value;
    }
  }

  return "";
}

export function parseCsvLine(line: string) {
  const values: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    const next = line[index + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        current += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === "," && !inQuotes) {
      values.push(current);
      current = "";
      continue;
    }

    current += char;
  }

  values.push(current);
  return values;
}

export function splitLooseDelimitedText(value: unknown) {
  return Array.from(
    new Set(
      String(value ?? "")
        .split(/[,\|\r\n]+/)
        .map((entry) => entry.trim())
        .filter(Boolean)
    )
  );
}

export function parseStaffCsvRows(csvContents: string) {
  const rows = csvContents
    .replace(/^\uFEFF/, "")
    .split(/\r?\n/)
    .filter((line) => line.trim() !== "");

  if (!rows.length) {
    return [] as StaffCsvRecord[];
  }

  const headers = parseCsvLine(rows[0]).map(normalizeCsvHeader);

  return rows.slice(1).map((line) => {
    const cells = parseCsvLine(line);
    return Object.fromEntries(headers.map((header, index) => [header, cells[index] ?? ""])) as StaffCsvRecord;
  });
}

export function getStaffCsvRowLabel(row: StaffCsvRecord) {
  const fullName = readFirstNonEmptyValue(row, ["full_name", "name"]);
  if (fullName) {
    return fullName;
  }

  const firstName = readFirstNonEmptyValue(row, ["first_name"]);
  const lastName = readFirstNonEmptyValue(row, ["last_name"]);
  const combinedName = `${firstName} ${lastName}`.trim();
  if (combinedName) {
    return combinedName;
  }

  return readFirstNonEmptyValue(row, ["email", "worker_code", "code", "id"]);
}

export function isImportableStaffCsvRow(row: StaffCsvRecord) {
  return Boolean(getStaffCsvRowLabel(row));
}

export function summarizeStaffCsv(csvContents: string) {
  const labels = parseStaffCsvRows(csvContents).map(getStaffCsvRowLabel).filter(Boolean);

  return {
    count: labels.length,
    sample: labels.slice(0, 3)
  };
}
